import { apiGet } from "./http";
import {
  replaceStoresFromApi,
  type Activity,
  type NewsItem,
} from "../pages/admin/utils/dataStore";
import type { AssocFile, Vote } from "./types";
import type { ActivitySummary } from "../pages/admin/utils/dataStore";
import { parseRegistrationOptionsFromApi } from "../utils/activityRegistrationOptions";
import { parseActivityTargetAudience } from "../utils/activityTargetAudience";
import { parseLotteryEnabledFromActivitySummaryJson } from "../utils/activitySummaryLottery";
import { activityToApiBody as buildActivityApiBody } from "./adminUpsertBodies";

/** API `eventEndDate` ISO → datetime-local 输入框 */
export function isoToDatetimeLocalValue(iso: string | undefined): string {
  if (!iso?.trim()) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** 活动/投票：API 返回 `yyyy-MM-dd`（仅日期）或 ISO → datetime-local */
export function apiDateToDatetimeLocalValue(s: string | undefined): string {
  if (!s?.trim()) return "";
  const t = s.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return `${t}T00:00`;
  return isoToDatetimeLocalValue(t);
}

/** 管理端表单日期时间 → API 用 UTC ISO（兼容仅日期 `yyyy-MM-dd`） */
export function activityOrVoteInstantToApiIso(raw: string): string {
  const t = raw.trim();
  if (!t) return t;
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) {
    const d = new Date(`${t}T00:00:00`);
    return Number.isNaN(d.getTime()) ? t : d.toISOString();
  }
  return datetimeLocalValueToIso(t) ?? t;
}

export function optionalActivityOrVoteInstantToApiIso(raw: string | undefined | null): string | null {
  const t = (raw ?? "").trim();
  if (!t) return null;
  return activityOrVoteInstantToApiIso(t);
}

/** datetime-local → 后端 CreateActivityBody.eventEndDate */
export function datetimeLocalValueToIso(local: string): string | null {
  const t = local.trim();
  if (!t) return null;
  const d = new Date(t);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/** 管理端「日期 + 时间」按浏览器本地时区解析，得到后端 ScheduledAt 用的 UTC ISO 字符串 */
export function localDateTimeToScheduledIso(date: string, time: string): string | null {
  const t = `${date.trim()}T${time.trim()}:00`;
  const d = new Date(t);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/** 后端 scheduledAt ISO → 本地 date / time 输入框（与 localDateTimeToScheduledIso 成对） */
export function scheduledIsoToLocalDateTime(iso: string): { date: string; time: string } {
  const d = new Date(iso.trim());
  if (Number.isNaN(d.getTime())) return { date: "", time: "09:00" };
  const pad = (n: number) => String(n).padStart(2, "0");
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

export async function loadDataStoreFromApi(tenantLegacyId: string): Promise<void> {
  const [actRaw, newsRaw, votes, filesRaw] = await Promise.all([
    apiGet<Record<string, unknown>[]>(`/api/tenants/${tenantLegacyId}/activities`),
    apiGet<Record<string, unknown>[]>(`/api/tenants/${tenantLegacyId}/news`),
    apiGet<Vote[]>(`/api/tenants/${tenantLegacyId}/votes`),
    apiGet<Record<string, unknown>[]>(`/api/tenants/${tenantLegacyId}/files`),
  ]);
  replaceStoresFromApi({
    activities: actRaw.map(mapActivity).filter((a) => !a.isLinkedActivity),
    news: newsRaw.map(mapNews),
    votes,
    files: filesRaw.map(mapFile),
  });
}

export function mapActivity(r: Record<string, unknown>): Activity {
  let activitySummary: ActivitySummary | undefined;
  const summaryJsonRaw =
    typeof r.activitySummaryJson === "string" && r.activitySummaryJson.length > 0
      ? r.activitySummaryJson
      : null;
  if (summaryJsonRaw) {
    try {
      activitySummary = JSON.parse(summaryJsonRaw) as ActivitySummary;
    } catch {
      /* ignore */
    }
  }
  return {
    id: String(r.id),
    title: String(r.title),
    activityType: String(r.activityType ?? ""),
    date: String(r.date ?? ""),
    eventEndDate:
      r.eventEndDate != null && String(r.eventEndDate).trim() !== ""
        ? String(r.eventEndDate)
        : r.endDate != null && String(r.endDate).trim() !== ""
          ? String(r.endDate)
          : undefined,
    location: String(r.location ?? ""),
    checkInFenceLat:
      r.checkInFenceLat != null && r.checkInFenceLat !== "" ? Number(r.checkInFenceLat) : undefined,
    checkInFenceLng:
      r.checkInFenceLng != null && r.checkInFenceLng !== "" ? Number(r.checkInFenceLng) : undefined,
    checkInFenceRadiusMeters:
      r.checkInFenceRadiusMeters != null && r.checkInFenceRadiusMeters !== ""
        ? Number(r.checkInFenceRadiusMeters)
        : undefined,
    checkInEarlyMinutes:
      r.checkInEarlyMinutes != null && r.checkInEarlyMinutes !== ""
        ? Number(r.checkInEarlyMinutes)
        : undefined,
    checkInLateMinutes:
      r.checkInLateMinutes != null && String(r.checkInLateMinutes).trim() !== ""
        ? Number(r.checkInLateMinutes)
        : null,
    requireCheckOut: Boolean(r.requireCheckOut),
    checkOutAllowEarly: Boolean(r.checkOutAllowEarly),
    checkOutEarlyMinutes:
      r.checkOutEarlyMinutes != null && r.checkOutEarlyMinutes !== ""
        ? Number(r.checkOutEarlyMinutes)
        : undefined,
    capacity: Number(r.capacity ?? 0),
    enrolled: Number(r.enrolled ?? 0),
    memberEnrolled:
      r.memberEnrolled != null && String(r.memberEnrolled).trim() !== ""
        ? Number(r.memberEnrolled)
        : undefined,
    guestEnrolled:
      r.guestEnrolled != null && String(r.guestEnrolled).trim() !== ""
        ? Number(r.guestEnrolled)
        : undefined,
    status: r.status as Activity["status"],
    regStartDate: String(r.regStartDate ?? ""),
    regEndDate: String(r.regEndDate ?? ""),
    expenseType: String(r.expenseType ?? ""),
    onlinePayAmountFen:
      r.onlinePayAmountFen != null && r.onlinePayAmountFen !== "" ? Number(r.onlinePayAmountFen) : 0,
    allowCancelWithRefund: Boolean(r.allowCancelWithRefund),
    cancelDeadlineMode: r.cancelDeadlineMode ? String(r.cancelDeadlineMode) : undefined,
    cancelDeadlineAt: r.cancelDeadlineAt ? String(r.cancelDeadlineAt) : undefined,
    cancelDeadlineResolvedAt: r.cancelDeadlineResolvedAt ? String(r.cancelDeadlineResolvedAt) : undefined,
    registrationOptions: parseRegistrationOptionsFromApi(r.registrationOptions),
    allowMemberFriendGuestRegistration: Boolean(r.allowMemberFriendGuestRegistration),
    memberFriendGuestRegistrationQuota:
      r.memberFriendGuestRegistrationQuota != null
        ? Number(r.memberFriendGuestRegistrationQuota)
        : undefined,
    showEnrollmentCountToMembers: r.showEnrollmentCountToMembers !== false,
    showShareOnRegistrationPage: r.showShareOnRegistrationPage !== false,
    targetAudience: parseActivityTargetAudience(r.targetAudience),
    linkedAssociations: Array.isArray(r.linkedAssociations)
      ? (r.linkedAssociations as Array<Record<string, unknown>>)
          .filter((x) => x && x.tenantId)
          .map((x) => ({
            tenantId: String(x.tenantId),
            tenantName: String(x.tenantName ?? x.shortName ?? x.tenantId),
            shortName: x.shortName ? String(x.shortName) : undefined,
            quota: Number(x.quota ?? 0) || 0,
            enrolled: x.enrolled != null ? Number(x.enrolled) : undefined,
          }))
      : undefined,
    isLinkedActivity: Boolean(r.isLinkedActivity),
    description: r.description ? String(r.description) : undefined,
    tags: r.tags ? String(r.tags) : undefined,
    participationPoints:
      r.participationPoints != null && r.participationPoints !== ""
        ? Number(r.participationPoints)
        : undefined,
    enableParticipationPointDeduction: Boolean(r.enableParticipationPointDeduction),
    notRegisteredDeductionPoints:
      r.notRegisteredDeductionPoints != null ? Number(r.notRegisteredDeductionPoints) : undefined,
    notCheckedInDeductionPoints:
      r.notCheckedInDeductionPoints != null ? Number(r.notCheckedInDeductionPoints) : undefined,
    coverUrl: r.coverUrl ? String(r.coverUrl) : undefined,
    requireDocSign: Boolean(r.requireDocSign),
    requiredDocIds: Array.isArray(r.requiredDocIds) ? (r.requiredDocIds as string[]) : undefined,
    activitySummary,
    activitySummaryJson: summaryJsonRaw,
    lotteryEnabled: parseLotteryEnabledFromActivitySummaryJson(summaryJsonRaw),
    rejectionNote: r.rejectionNote ? String(r.rejectionNote) : undefined,
    approvedAt: r.approvedAt ? String(r.approvedAt) : undefined,
    ageMin: r.ageMin != null && r.ageMin !== "" ? Number(r.ageMin) : undefined,
    ageMax: r.ageMax != null && r.ageMax !== "" ? Number(r.ageMax) : undefined,
  };
}

/** 与后端 CreateActivityBody 对齐（camelCase JSON） */
export function activityToApiBody(a: Activity): Record<string, unknown> {
  const summaryJson =
    a.activitySummaryJson ??
    (a.activitySummary ? JSON.stringify(a.activitySummary) : null);
  return buildActivityApiBody({
    title: a.title,
    activityType: a.activityType,
    date: a.date,
    eventEndDate: a.eventEndDate ?? null,
    location: a.location || "待定",
    checkInFenceLat: a.checkInFenceLat ?? null,
    checkInFenceLng: a.checkInFenceLng ?? null,
    checkInFenceRadiusMeters: a.checkInFenceRadiusMeters ?? null,
    checkInEarlyMinutes: a.checkInEarlyMinutes ?? null,
    checkInLateMinutes: a.checkInLateMinutes ?? null,
    requireCheckOut: a.requireCheckOut ?? null,
    checkOutAllowEarly: a.checkOutAllowEarly ?? null,
    checkOutEarlyMinutes: a.checkOutEarlyMinutes ?? null,
    capacity: a.capacity,
    enrolled: a.enrolled,
    status: a.status,
    regStartDate: a.regStartDate ?? null,
    regEndDate: a.regEndDate ?? null,
    expenseType: a.expenseType ?? "",
    onlinePayAmountFen: a.onlinePayAmountFen ?? 0,
    allowCancelWithRefund: a.allowCancelWithRefund ?? null,
    cancelDeadlineMode: a.cancelDeadlineMode ?? null,
    cancelDeadlineAt: a.cancelDeadlineAt ?? null,
    cancelDeadlineResolvedAt: a.cancelDeadlineResolvedAt ?? null,
    registrationOptions: a.registrationOptions ?? null,
    allowMemberFriendGuestRegistration: a.allowMemberFriendGuestRegistration ?? null,
    memberFriendGuestRegistrationQuota: a.memberFriendGuestRegistrationQuota ?? null,
    showEnrollmentCountToMembers: a.showEnrollmentCountToMembers ?? null,
    showShareOnRegistrationPage: a.showShareOnRegistrationPage ?? null,
    targetAudience: a.targetAudience ?? null,
    description: a.description ?? null,
    tags: a.tags ?? null,
    participationPoints: a.participationPoints ?? null,
    enableParticipationPointDeduction: a.enableParticipationPointDeduction ?? null,
    notRegisteredDeductionPoints: a.notRegisteredDeductionPoints ?? null,
    notCheckedInDeductionPoints: a.notCheckedInDeductionPoints ?? null,
    coverUrl: a.coverUrl ?? null,
    requireDocSign: a.requireDocSign ?? null,
    requiredDocIds: a.requiredDocIds ?? null,
    activitySummaryJson: summaryJson,
    rejectionNote: a.rejectionNote ?? null,
    approvedAt: a.approvedAt ?? null,
    ageMin: a.ageMin ?? null,
    ageMax: a.ageMax ?? null,
  });
}

export function voteToUpsertBody(v: Vote): Record<string, unknown> {
  const scope = v.targetScope;
  return {
    title: v.title,
    description: v.description ?? "",
    status: v.status,
    startDate: activityOrVoteInstantToApiIso(v.startDate),
    endDate: activityOrVoteInstantToApiIso(v.endDate),
    totalVoters: v.totalVoters ?? 0,
    allowMultiple: v.allowMultiple,
    isAnonymous: v.isAnonymous,
    showToMembers: v.showToMembers,
    coverUrl: v.coverUrl ?? null,
    targetScopeType: scope?.type ?? null,
    targetActivityId: scope?.activityId ?? null,
    targetActivityTitle: scope?.activityTitle ?? null,
    scheduledPublishAt: v.scheduledPublishAt ?? null,
    participationPoints: v.participationPoints ?? 0,
    options: v.options.map((o) => ({
      legacyOptionId: o.id,
      text: o.text,
      votes: o.votes ?? 0,
      imageUrl: o.imageUrl ?? null,
      description: o.description ?? null,
    })),
  };
}

export function mapNews(r: Record<string, unknown>): NewsItem {
  return {
    id: String(r.id),
    title: String(r.title),
    category: String(r.category),
    author: String(r.author),
    status: String(r.status),
    publishDate: String(r.publishDate),
    readCount: Number(r.readCount ?? 0),
    summary: r.summary ? String(r.summary) : undefined,
    content: r.content ? String(r.content) : undefined,
    coverUrl: r.coverUrl ? String(r.coverUrl) : undefined,
    scheduledAt: r.scheduledAt ? String(r.scheduledAt) : undefined,
  };
}

/** 与后端 CreateNewsBody 对齐 */
export function newsToApiBody(n: NewsItem, overrides?: Partial<NewsItem>): Record<string, unknown> {
  const x = { ...n, ...overrides };
  return {
    title: x.title,
    category: x.category,
    author: x.author?.trim() ? x.author : "超级管理员",
    status: x.status,
    publishDate: x.publishDate,
    readCount: x.readCount ?? 0,
    summary: x.summary ?? null,
    content: x.content ?? null,
    coverUrl: x.coverUrl ?? null,
    scheduledAt: x.scheduledAt ?? null,
    featured: false,
  };
}

export function mapFile(r: Record<string, unknown>): AssocFile {
  const cu = r.contentUrl;
  const contentUrl =
    cu != null && String(cu).trim() !== "" ? String(cu).trim() : undefined;
  return {
    id: String(r.id),
    name: String(r.name),
    type: r.type as AssocFile["type"],
    size: String(r.size),
    uploadedAt: String(r.uploadedAt),
    uploader: String(r.uploader),
    version: String(r.version),
    showToMembers: Boolean(r.showToMembers),
    requireSignature: Boolean(r.requireSignature),
    signedCount: Number(r.signedCount ?? 0),
    totalMembers: Number(r.totalMembers ?? 0),
    status: r.status as AssocFile["status"],
    description: String(r.description ?? ""),
    contentUrl,
  };
}
